import { computed, reactive } from 'vue';
import * as z from 'zod/mini';

import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { storageRefWithSchema } from '@nzyme/vue-utils/reactivity/storageRefWithSchema.js';
import type { Id } from '@sigma/common/schema/Id.js';

export type AuthGuestState = z.infer<typeof AuthGuestState>;
export const AuthGuestState = z.object({
    /** Guest ID is stored as a string in local storage */
    guestId: z.string(),
    token: z.string(),
});

/**
 * Service for storing guest (unauthenticated) user identities.
 * Guest creation is handled by AuthGuestProvider to avoid circular dependencies.
 */
export const AuthGuestStore = defineService({
    name: 'AuthGuestStore',
    deps: {
        logger: Logger,
    },
    setup({ logger }) {
        const guestRef = storageRefWithSchema({
            key: `patient:guest`,
            sync: 'always',
            schema: z.nullable(AuthGuestState),
            default: () => null,
        });

        return reactive({
            guestId: computed(() => guestRef.value?.guestId),
            token: computed(() => guestRef.value?.token),
            setGuest,
            clearGuest,
        });

        /**
         * Set guest identity from API response.
         */
        function setGuest(guestId: Id, token: string) {
            guestRef.value = { guestId: String(guestId), token };
            logger.debug('Guest identity set', { guestId });
        }

        /**
         * Clear the current guest identity.
         */
        function clearGuest() {
            logger.debug('Clearing guest identity');
            guestRef.value = null;
        }
    },
});
